import { PortfolioContextItem, memberData } from '../models/Portfolio';
import React, { MouseEvent, useEffect, useState } from 'react';
import Select, { SelectChangeEvent } from '@mui/material/Select';

import { ApiPortfolioState } from '../../dashboard/models/ApiPortfolioState';
import AuthService from '../../shared/services/AuthService';
import { Button } from '@mui/material';
import ConfirmationDialog from '../../shared/components/ConfirmationDialog';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import DateService from '../../shared/services/DateService';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Unstable_Grid2';
import InputLabel from '@mui/material/InputLabel';
import LessonPortfolioService from '../services/LessonPortfolioService';
import { LessonStatusLabel } from '../../content/components/LessonStatusLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { UNFINALISE_ASSESSMENT } from '../../shared/constants';
import { isEmpty } from 'lodash';
import styles from '../styles/AssessmentLessonItem.module.css';
import { usePortfolio } from '../hooks/usePortfolio';

interface AssessmentLessonItemProps {
  assessmentPortfolio: PortfolioContextItem;
  assignLoading: boolean;
  userkey: number;
  isFinalised: boolean;
  onSelect: (portfolio: PortfolioContextItem) => void;
  onAssessorChange: (
    selectedPortfolio: PortfolioContextItem,
    memberkey: number
  ) => void;
}

export function AssessmentLessonItem(props: AssessmentLessonItemProps) {
  const { assessmentPortfolio, userkey, assignLoading } = props;

  const [selectedAssessor, setSelectedAssessor] = useState<number>(
    assessmentPortfolio.marker?.userkey || 0
  );

  const [unfinalise, setUnfinalise] = useState<boolean>(false);

  const loggedInUserkey = AuthService.Userkey;
  const assignedToMe =
    assessmentPortfolio.marker?.userkey === loggedInUserkey ||
    selectedAssessor === loggedInUserkey; //When changing assessor will check whether the user can open the portfolio
  const openPortfolio = LessonPortfolioService.canOpenPortfolio(assignedToMe);
  const assessorsData: memberData[] = assessmentPortfolio?.assessors;

  const {
    updatePortfolioState,
    updatingPortfolioState,
    updatePortfolioStateError
  } = usePortfolio(props.isFinalised);

  useEffect(() => {
    if (assessmentPortfolio) {
      setSelectedAssessor(assessmentPortfolio.marker?.userkey || 0);
    }
  }, [assessmentPortfolio]);

  function handleLessonCardClick(event: MouseEvent<HTMLDivElement>) {
    if (openPortfolio) {
      props.onSelect(assessmentPortfolio);
    }
  }

  function handleAssessorChange(event: SelectChangeEvent<number>) {
    setSelectedAssessor(+event.target.value);
    props.onAssessorChange(assessmentPortfolio, +event.target.value);
  }

  function handleAssessorClick(event: MouseEvent<HTMLDivElement>) {
    event.stopPropagation();
  }

  const assignedAssessor = assessorsData.find(
    (assessor) => assessor.userkey === selectedAssessor
  );

  const handleUnfinalise = () => {
    const { userkey, modulekey, layer } = assessmentPortfolio;
    updatePortfolioState(
      userkey,
      modulekey,
      layer,
      ApiPortfolioState.AssessmentMark
    );
  };

  return (
    <>
      {/* <Grid container> */}
      <Grid container xs={12} sm={9} md={10} direction="column">
        <Grid className={styles.grid} container justifyContent="space-between">
          <Grid>
            <Typography
              variant="body2"
              className={styles.typography}
            >{`${assessmentPortfolio.firstname} ${assessmentPortfolio.lastname}`}</Typography>
          </Grid>
          <Grid>
            <Typography variant="body2" className={styles.typography}>
              {DateService.formatDate(assessmentPortfolio.modified)}
            </Typography>
          </Grid>
        </Grid>
        <Paper
          className={openPortfolio ? styles.paperLink : styles.paper}
          onClick={handleLessonCardClick}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid>
              <Typography variant="body1">
                {assessmentPortfolio.module}
              </Typography>
            </Grid>
            <Grid>
              {!props.isFinalised ? (
                <FormControl
                  className={`${styles.formControl}`}
                  size="small"
                  variant="standard"
                >
                  <InputLabel id="assignTo-label">
                    <Typography variant="body2">Assigned To</Typography>
                  </InputLabel>

                  <Select
                    labelId="assignTo-label"
                    id="assignTo"
                    value={selectedAssessor}
                    displayEmpty
                    onChange={handleAssessorChange}
                    onClick={handleAssessorClick}
                    disabled={
                      assignLoading ||
                      assessmentPortfolio.state ===
                        ApiPortfolioState.AssessmentLock ||
                      isEmpty(assessorsData)
                    }
                  >
                    {assessorsData?.map((assessor: memberData) => (
                      <MenuItem key={assessor.userkey} value={assessor.userkey}>
                        <Typography variant="button">
                          {assessor.userkey === userkey
                            ? 'ME'
                            : `${assessor.firstname.toUpperCase()} ${assessor.lastname.toUpperCase()}`}
                        </Typography>
                      </MenuItem>
                    ))}
                    <MenuItem value="0">
                      <Typography variant="button">UNASSIGNED</Typography>
                    </MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    className={styles.typographyAssigned}
                  >
                    Assigned To
                  </Typography>
                  <Typography variant="button">
                    {assignedAssessor
                      ? assignedAssessor.userkey === userkey
                        ? 'ME'
                        : `${assignedAssessor?.firstname.toUpperCase()} ${assignedAssessor?.lastname.toUpperCase()}`
                      : 'UNASSIGNED'}
                  </Typography>
                </>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid
        container
        xs={12}
        sm={3}
        md={2}
        justifyContent="center"
        alignItems="center"
      >
        {!props.isFinalised ? (
          !assignLoading ? (
            <LessonStatusLabel
              displayMode={'mark'}
              state={assessmentPortfolio.state}
              completed={!!assessmentPortfolio.completed}
              showBorder={false}
            />
          ) : (
            <CustomLoadingProgress size={20} />
          )
        ) : (
          <Button
            variant="contained"
            color="primary"
            className={styles.buttonUnfinalise}
            onClick={() => setUnfinalise(true)}
          >
            Unfinalise
          </Button>
        )}
        {/* </Grid> */}
      </Grid>
      {unfinalise && (
        <ConfirmationDialog
          open={true}
          message={UNFINALISE_ASSESSMENT}
          onClose={(agreed: boolean) => {
            setUnfinalise(false);
            if (!agreed) {
              return;
            }
            handleUnfinalise();
          }}
        />
      )}
    </>
  );
}
